import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import MinimalFooter from '@/components/MinimalFooter';
import { PresetInfo } from '@/components/PresetCard';
import Player from '@/components/Player';
import { AmbienceSound } from '@/components/AmbienceMixer';
import { BinauralBeatGenerator, AmbientSoundPlayer } from '@/utils/audioUtils';
import { toast } from 'sonner';
import { CloudRain, Waves, Wind, Trees, CloudFog, ArrowRight, Cloud, Brain, Headphones } from 'lucide-react';
import { GuidedSessionInfo } from '@/components/GuidedSessions';
import TabView from '@/components/TabView';
import { Card, CardContent } from '@/components/ui/card';
import { SessionStep } from '@/components/AdvancedSession';
import AnimatedHeader from '@/components/AnimatedHeader';
import { getSoundUrl, ambientSoundPresets } from '@/config/audioConfig';
import { trackEvent } from '@/utils/analytics';
import { EventType } from '@/types/analytics';
import { ALL_PRESETS } from '@/config/presetConfig';
import { CustomSession } from '@/types/session';

interface AdvancedSettings {
  leftFrequency: number;
  rightFrequency: number;
  carrierFrequency: number;
  leftWaveform?: string;
  rightWaveform?: string;
}

const AMBIENT_SOUNDS: AmbienceSound[] = ambientSoundPresets.map(preset => ({
  id: preset.id,
  name: preset.name,
  icon: preset.icon || Cloud,
  volume: 0,
  path: preset.path
}));

export default function Index() {
  const [activePreset, setActivePreset] = useState<PresetInfo | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [ambientSounds, setAmbientSounds] = useState<AmbienceSound[]>(AMBIENT_SOUNDS);
  const [volume, setVolume] = useState(33);
  const [masterVolume, setMasterVolume] = useState(33);
  const [isProgressiveSession, setIsProgressiveSession] = useState(false);
  const [progressiveData, setProgressiveData] = useState<{
    startFreq: number;
    endFreq: number;
    duration: number;
    transitionTime: number;
  } | null>(null);
  const [advancedSessionSteps, setAdvancedSessionSteps] = useState<SessionStep[] | null>(null);
  const [currentAdvancedStep, setCurrentAdvancedStep] = useState<number>(0);
  const [isPlayMode, setIsPlayMode] = useState(false);
  const [isProcessingPlayPause, setIsProcessingPlayPause] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [activeTab, setActiveTab] = useState('presets');
  const [isTourOpen, setIsTourOpen] = useState(false);
  
  const binauralGeneratorRef = useRef<BinauralBeatGenerator | null>(null);
  const ambientPlayerRef = useRef<AmbientSoundPlayer | null>(null);
  const progressiveIntervalRef = useRef<number | null>(null);
  const advancedIntervalRef = useRef<number | null>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    binauralGeneratorRef.current = new BinauralBeatGenerator();
    ambientPlayerRef.current = new AmbientSoundPlayer();
    
    setTimeout(() => {
      // Load all sounds from ambientSoundPresets
      ambientSoundPresets.forEach(sound => {
        if (ambientPlayerRef.current) {
          ambientPlayerRef.current.loadSound(sound.id, sound.path);
          console.log(`Loading ambient sound: ${sound.id} from ${sound.path}`);
        }
      });
      
      console.log("Audio system initialized. Using audioConfig.ts for sound URLs.");
    }, 500);
    
    return () => {
      if (binauralGeneratorRef.current) {
        binauralGeneratorRef.current.stop();
      }
      if (ambientPlayerRef.current) {
        ambientPlayerRef.current.stopAll();
      }
      if (progressiveIntervalRef.current) {
        clearInterval(progressiveIntervalRef.current);
      }
      if (advancedIntervalRef.current) {
        clearInterval(advancedIntervalRef.current);
      }
    };
  }, []);
  
  const togglePlayMode = () => {
    setIsPlayMode(!isPlayMode);
    
    if (!isPlayMode && playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    if (!isPlayMode) {
      document.body.classList.add('focus-mode-active');
    } else {
      document.body.classList.remove('focus-mode-active');
    }
  };
  
  const updateSessionHistory = (preset: PresetInfo) => {
    if (localStorage.getItem('isLoggedIn') !== 'true') return;
    
    const newHistoryEntry = {
      preset: preset,
      date: new Date()
    };
    
    const savedHistory = localStorage.getItem('history');
    let history = [];
    
    if (savedHistory) {
      history = JSON.parse(savedHistory);
    }
    
    const updatedHistory = [
      {
        preset: newHistoryEntry.preset,
        date: newHistoryEntry.date.toISOString()
      },
      ...history.slice(0, 9)
    ];
    
    localStorage.setItem('history', JSON.stringify(updatedHistory));
  };
  
  const handlePlayPause = async () => {
    if (isProcessingPlayPause) return;
    
    setIsProcessingPlayPause(true);
    
    try {
      if (!activePreset) return;
      
      if (isPlaying) {
        if (binauralGeneratorRef.current) {
          await binauralGeneratorRef.current.stop();
        }
        if (ambientPlayerRef.current) {
          // Just stop all ambient sounds without changing their volumes
          ambientPlayerRef.current.stopAll();
        }
        if (progressiveIntervalRef.current) {
          clearInterval(progressiveIntervalRef.current);
          progressiveIntervalRef.current = null;
        }
        if (advancedIntervalRef.current) {
          clearInterval(advancedIntervalRef.current);
          advancedIntervalRef.current = null;
        }
        setIsPlaying(false);
        setIsPreviewing(false);
        
        // Track session paused event
        trackEvent(EventType.SESSION_PAUSED, {
          frequency: activePreset.frequency,
          presetId: activePreset.id,
          presetName: activePreset.name,
          sessionType: activePreset.advancedSettings ? 'advanced' : 'standard'
        });
      } else {
        if (binauralGeneratorRef.current) {
          console.log("Starting playback with settings:", {
            frequency: activePreset.frequency,
            isAdvanced: !!activePreset.advancedSettings,
            advancedSettings: activePreset.advancedSettings
          });

          // Reset waveforms to default for basic mode
          if (!activePreset.advancedSettings) {
            binauralGeneratorRef.current.setWaveforms('sine', 'sine');
          }

          await binauralGeneratorRef.current.play(
            activePreset.frequency,
            activePreset.advancedSettings?.leftWaveform,
            activePreset.advancedSettings?.rightWaveform,
            activePreset.advancedSettings?.carrierFrequency
          );
          const effectiveVolume = (volume * masterVolume) / 100;
          binauralGeneratorRef.current.setVolume(effectiveVolume);
        }
        if (ambientPlayerRef.current) {
          // Initialize volumes for all ambient sounds before playing
          ambientSounds.forEach(sound => {
            if (sound.volume > 0) {
              const effectiveVolume = (sound.volume * masterVolume) / 100;
              ambientPlayerRef.current?.setVolume(sound.id, effectiveVolume);
            }
          });
          await ambientPlayerRef.current.playAll();
        }
        setIsPlaying(true);
        setIsPreviewing(false);
        
        // Track session started or resumed event
        trackEvent(EventType.SESSION_STARTED, {
          frequency: activePreset.frequency,
          presetId: activePreset.id,
          presetName: activePreset.name,
          sessionType: activePreset.advancedSettings ? 'advanced' : 'standard',
          waveformLeft: activePreset.advancedSettings?.leftWaveform || 'sine',
          waveformRight: activePreset.advancedSettings?.rightWaveform || 'sine',
          volume: volume,
          masterVolume: masterVolume,
          activeAmbientSounds: ambientSounds
            .filter(sound => sound.volume > 0)
            .map(sound => ({ id: sound.id, volume: sound.volume }))
        });
      }
    } catch (error) {
      console.error("Error in handlePlayPause:", error);
      trackEvent(EventType.PLAYBACK_ERROR, {
        error: error instanceof Error ? error.message : 'Unknown error',
        frequency: activePreset?.frequency,
        presetId: activePreset?.id,
        presetName: activePreset?.name
      });
    } finally {
      setIsProcessingPlayPause(false);
    }
  };
  
  const handlePresetSelect = (preset: PresetInfo) => {
    // Stop any current playback
    if (binauralGeneratorRef.current) {
      binauralGeneratorRef.current.stop();
    }
    if (ambientPlayerRef.current) {
      ambientPlayerRef.current.stopAll();
    }
    setIsPlaying(false);
    
    // Reset ambient sounds to all volumes at 0
    const initialAmbientSounds = ambientSoundPresets.map(configSound => ({
      id: configSound.id,
      name: configSound.name,
      icon: configSound.icon || Cloud,
      volume: 0, // Always start with volume 0
      path: configSound.path,
      category: configSound.category
    }));
    
    // If preset has ambient sounds, update their volumes
    if (preset.ambientSounds && preset.ambientSounds.length > 0) {
      const updatedAmbientSounds = initialAmbientSounds.map(sound => {
        const presetSound = preset.ambientSounds?.find(s => s.id === sound.id);
        return presetSound ? { ...sound, volume: presetSound.volume } : sound;
      });
      setAmbientSounds(updatedAmbientSounds);
      
      // Ensure all sounds are properly loaded and initialized
      if (ambientPlayerRef.current) {
        preset.ambientSounds.forEach(sound => {
          const configSound = ambientSoundPresets.find(s => s.id === sound.id);
          if (configSound) {
            ambientPlayerRef.current?.loadSound(sound.id, configSound.path);
            const effectiveVolume = (sound.volume * masterVolume) / 100;
            ambientPlayerRef.current?.setVolume(sound.id, effectiveVolume);
            // Stop any playing sounds
            ambientPlayerRef.current?.stopSound(sound.id);
          }
        });
      }
    } else {
      // If no ambient sounds in preset, set all volumes to 0
      setAmbientSounds(initialAmbientSounds);
      
      // Stop all sounds if no ambient sounds in preset
      if (ambientPlayerRef.current) {
        ambientPlayerRef.current.stopAll();
      }
    }
    
    // Reset carrier frequency and waveforms to default for basic mode
    if (!preset.advancedSettings && binauralGeneratorRef.current) {
      binauralGeneratorRef.current.setCarrierFrequency(200);
      binauralGeneratorRef.current.setWaveforms('sine', 'sine');
    }
    
    setActivePreset(preset);
    
    // Track preset selection
    trackEvent(EventType.PRESET_SELECTED, {
      presetId: preset.id,
      presetName: preset.name,
      frequency: preset.frequency,
      duration: preset.duration
    });
  };
  
  const createRandomPreset = (): PresetInfo => {
    const brainwaveRanges = [
      { name: 'Delta', min: 0.5, max: 4, description: 'Deep sleep state' },
      { name: 'Theta', min: 4, max: 8, description: 'Meditative state' },
      { name: 'Alpha', min: 8, max: 12, description: 'Relaxed state' },
      { name: 'Beta', min: 12, max: 30, description: 'Active, focused state' },
      { name: 'Gamma', min: 30, max: 50, description: 'Heightened perception' }
    ];
    
    const randomRange = brainwaveRanges[Math.floor(Math.random() * brainwaveRanges.length)];
    
    const randomFrequency = randomRange.min + Math.random() * (randomRange.max - randomRange.min);
    
    const randomDuration = Math.floor(Math.random() * 40) + 5;
    
    const randomColor = `#${Math.floor(Math.random()*16777215).toString(16)}`;
    
    return {
      id: 'random',
      name: `Random ${randomRange.name}`,
      frequency: randomFrequency,
      duration: randomDuration,
      description: `${randomRange.description} at ${randomFrequency.toFixed(1)} Hz`,
      color: randomColor
    };
  };
  
  const startProgressiveSession = () => {
    if (!progressiveData || !binauralGeneratorRef.current) return;
    
    const { startFreq, endFreq, duration, transitionTime } = progressiveData;
    
    binauralGeneratorRef.current.play(startFreq);
    const effectiveVolume = (masterVolume) / 100;
    binauralGeneratorRef.current.setVolume(effectiveVolume);
    
    const totalSteps = transitionTime * 60;
    const frequencyDiff = endFreq - startFreq;
    const stepChange = frequencyDiff / totalSteps;
    
    let currentStep = 0;
    let currentFreq = startFreq;
    
    progressiveIntervalRef.current = window.setInterval(() => {
      currentStep++;
      currentFreq += stepChange;
      
      if (binauralGeneratorRef.current) {
        binauralGeneratorRef.current.setFrequency(currentFreq);
      }
      
      if (currentStep >= totalSteps) {
        if (progressiveIntervalRef.current) {
          clearInterval(progressiveIntervalRef.current);
          progressiveIntervalRef.current = null;
        }
      }
    }, 1000) as unknown as number;
  };
  
  const handleVolumeChange = (value: number) => {
    setVolume(value);
    if (binauralGeneratorRef.current && isPlaying) {
      const effectiveVolume = (value * masterVolume) / 100;
      binauralGeneratorRef.current.setVolume(effectiveVolume);
      console.log(`Setting binaural beat volume to ${effectiveVolume}% (${value}% × ${masterVolume}% master)`);
    }
  };
  
  const handleMasterVolumeChange = (value: number) => {
    setMasterVolume(value);
    
    if (binauralGeneratorRef.current && isPlaying) {
      const effectiveVolume = (volume * value) / 100;
      binauralGeneratorRef.current.setVolume(effectiveVolume);
      console.log(`Setting binaural beat volume to ${effectiveVolume}% (${volume}% × ${value}% master)`);
    }
    
    if (ambientPlayerRef.current) {
      ambientSounds.forEach(sound => {
        if (sound.volume > 0) {
          const effectiveVolume = (sound.volume * value) / 100;
          ambientPlayerRef.current?.setVolume(sound.id, effectiveVolume);
          console.log(`Setting ${sound.id} volume to ${effectiveVolume}% (${sound.volume}% × ${value}% master)`);
        }
      });
    }
  };
  
  const handleAmbientVolumeChange = (id: string, value: number) => {
    setAmbientSounds(prevSounds => 
      prevSounds.map(sound => 
        sound.id === id ? { ...sound, volume: value } : sound
      )
    );
    
    if (ambientPlayerRef.current) {
      const effectiveVolume = (value * masterVolume) / 100;
      ambientPlayerRef.current.setVolume(id, effectiveVolume);
      console.log(`Setting ${id} volume to ${effectiveVolume}% (${value}% × ${masterVolume}% master)`);
      
      // Only play/stop sounds if we're in an active session or previewing
      if ((isPlaying || isPreviewing) && value > 0) {
        ambientPlayerRef.current.playSound(id);
      } else {
        // Always stop the sound if we're not in a session or preview
        ambientPlayerRef.current.stopSound(id);
      }
    }
    
    trackEvent(EventType.AMBIENT_VOLUME_CHANGED, {
      soundId: id,
      soundVolume: value
    });
  };
  
  const handleNextPreset = () => {
    const currentIndex = activePreset ? ALL_PRESETS.findIndex(p => p.id === activePreset.id) : -1;
    if (currentIndex >= 0 && currentIndex < ALL_PRESETS.length - 1) {
      handlePresetSelect(ALL_PRESETS[currentIndex + 1]);
    }
  };
  
  const handlePreviousPreset = () => {
    const currentIndex = activePreset ? ALL_PRESETS.findIndex(p => p.id === activePreset.id) : -1;
    if (currentIndex > 0) {
      handlePresetSelect(ALL_PRESETS[currentIndex - 1]);
    }
  };
  
  const handleSessionComplete = () => {
    if (binauralGeneratorRef.current) {
      binauralGeneratorRef.current.stop();
    }
    if (ambientPlayerRef.current) {
      ambientPlayerRef.current.stopAll();
    }
    if (progressiveIntervalRef.current) {
      clearInterval(progressiveIntervalRef.current);
      progressiveIntervalRef.current = null;
    }
    if (advancedIntervalRef.current) {
      clearInterval(advancedIntervalRef.current);
      advancedIntervalRef.current = null;
    }
    setIsPlaying(false);
    setIsPreviewing(false);
    
    // Exit focus mode if active
    if (isPlayMode) {
      setIsPlayMode(false);
      document.body.classList.remove('focus-mode-active');
    }
    
    // Track session completed event
    trackEvent(EventType.SESSION_COMPLETED, {
      presetId: activePreset?.id,
      presetName: activePreset?.name,
      frequency: activePreset?.frequency,
      sessionType: activePreset?.advancedSettings ? 'advanced' : 'standard',
      activeAmbientSounds: ambientSounds
        .filter(sound => sound.volume > 0)
        .map(sound => ({ id: sound.id, volume: sound.volume }))
    });
    
    toast.success('Session completed', {
      description: 'Your binaural beats session has finished'
    });
  };
  
  const handleCreateCustomSession = (frequency: number, duration: number, title: string, advancedSettings?: AdvancedSettings) => {
    // Create a custom preset
    const customPreset: PresetInfo = {
      id: 'custom',
      name: title,
      frequency,
      duration,
      description: 'Custom session created from chat recommendation',
      color: '#4C8DAA',
      ...(advancedSettings && { advancedSettings }),
      ambientSounds: ambientSoundPresets.map(sound => {
        // Find the current state of this sound
        const currentSound = ambientSounds.find(s => s.id === sound.id);
        return {
          id: sound.id,
          volume: currentSound?.volume || 0 // Use current volume if set, otherwise 0
        };
      })
    };

    // Initialize ambient sounds with current volumes
    const initialAmbientSounds = ambientSoundPresets.map(configSound => {
      const currentSound = ambientSounds.find(s => s.id === configSound.id);
      return {
        id: configSound.id,
        name: configSound.name,
        icon: configSound.icon || Cloud,
        volume: currentSound?.volume || 0, // Use current volume if set, otherwise 0
        path: configSound.path,
        category: configSound.category
      };
    });

    // Set ambient sounds state
    setAmbientSounds(initialAmbientSounds);

    // Stop any currently playing sounds
    if (ambientPlayerRef.current) {
      ambientPlayerRef.current.stopAll();
    }

    setActivePreset(customPreset);
    setIsPlaying(false);

    // Scroll to player
    if (playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    // Track custom session creation
    trackEvent(EventType.CUSTOM_SESSION_CREATED, {
      frequency,
      duration,
      title,
      isAdvanced: !!advancedSettings
    });
  };
  
  const handleChatCustomSession = (session: CustomSession) => {
    // Create a custom preset with proper ambient sound volumes
    const customPreset: PresetInfo = {
      id: 'custom',
      name: 'Custom Session',
      frequency: session.frequency,
      duration: session.duration,
      description: `Custom session with ${session.frequency} Hz beat frequency${session.advancedSettings?.carrierFrequency ? ` and ${session.advancedSettings.carrierFrequency} Hz carrier frequency` : ''}`,
      color: '#4C8DAA',
      advancedSettings: session.advancedSettings,
      ambientSounds: session.ambientSounds.map(id => {
        const configSound = ambientSoundPresets.find(s => s.id === id);
        return {
          id,
          volume: configSound?.defaultVolume || 50 // Use config default or fallback to 50
        };
      })
    };

    // Initialize ambient sounds with all volumes at 0 by default
    const initialAmbientSounds = ambientSoundPresets.map(configSound => ({
      id: configSound.id,
      name: configSound.name,
      icon: configSound.icon || Cloud,
      volume: 0, // Always start with volume 0
      path: configSound.path,
      category: configSound.category
    }));

    // Only set volumes for explicitly configured sounds
    if (session.ambientSounds && session.ambientSounds.length > 0) {
      const updatedAmbientSounds = initialAmbientSounds.map(sound => {
        if (session.ambientSounds.includes(sound.id)) {
          const configSound = ambientSoundPresets.find(s => s.id === sound.id);
          return {
            ...sound,
            volume: configSound?.defaultVolume || 50 // Use config default or fallback to 50
          };
        }
        return sound;
      });
      setAmbientSounds(updatedAmbientSounds);
    } else {
      setAmbientSounds(initialAmbientSounds);
    }

    setActivePreset(customPreset);
    setIsPlaying(false);

    // Scroll to player
    if (playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    // Track custom session creation
    trackEvent(EventType.CUSTOM_SESSION_CREATED, {
      frequency: session.frequency,
      duration: session.duration,
      ambientSounds: session.ambientSounds,
      soundscapes: session.soundscapes
    });
  };
  
  const handleCreateProgressiveSession = (
    startFreq: number, 
    endFreq: number, 
    duration: number, 
    transitionTime: number
  ) => {
    const progressivePreset: PresetInfo = {
      id: 'progressive',
      name: 'Progressive Session',
      frequency: startFreq,
      duration: duration,
      description: `Gradually shifts from ${startFreq} Hz to ${endFreq} Hz`,
      color: '#9B87F5'
    };
    
    setProgressiveData({
      startFreq,
      endFreq,
      duration,
      transitionTime
    });
    
    setIsProgressiveSession(true);
    setActivePreset(progressivePreset);
    
    if (playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    setTimeout(() => {
      setIsPlaying(true);
      startProgressiveSession();
      if (ambientPlayerRef.current) {
        ambientPlayerRef.current.playAll();
      }
      
      toast.success('Progressive session started', {
        description: `Shifting from ${startFreq} Hz to ${endFreq} Hz over ${transitionTime} minutes`
      });
    }, 300);
  };
  
  const handleSelectGuidedSession = (session: GuidedSessionInfo) => {
    const guidedPreset: PresetInfo = {
      id: session.id,
      name: session.title,
      frequency: session.frequency,
      duration: session.duration,
      description: session.description,
      color: session.category === 'meditation' 
        ? '#8A6AFE' 
        : session.category === 'focus' 
          ? '#5B6AFE' 
          : '#6AC3FE'
    };
    
    handlePresetSelect(guidedPreset);
    setTimeout(() => {
      handlePlayPause();
      
      toast.success(`Playing guided session: ${session.title}`, {
        description: `This session includes voice guidance with ${session.frequency} Hz binaural beats`
      });
    }, 300);
  };
  
  const handleStartRecording = (): boolean => {
    if (!binauralGeneratorRef.current || !isPlaying) return false;
    return binauralGeneratorRef.current.startRecording();
  };
  
  const handleStopRecording = async (): Promise<Blob | null> => {
    if (!binauralGeneratorRef.current) return null;
    
    try {
      const blob = await binauralGeneratorRef.current.stopRecording();
      return blob;
    } catch (error) {
      console.error("Error stopping recording:", error);
      return null;
    }
  };
  
  const handleDirectDownload = async (presetName: string, frequency: number, duration: number): Promise<void> => {
    if (!binauralGeneratorRef.current) {
      toast.error("Audio system not initialized");
      return;
    }
    
    try {
      const durationInSeconds = duration;
      
      toast.info("Generating audio file", {
        description: `Creating a ${Math.floor(durationInSeconds / 60)} minute binaural beat file...`
      });
      
      const blob = await binauralGeneratorRef.current.generateBinauralBeat(frequency, durationInSeconds);
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const sanitizedName = presetName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      a.href = url;
      a.download = `binauralflow_${sanitizedName}_${frequency}hz_${timestamp}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Download started", {
        description: `Your ${frequency}Hz binaural beat file is being downloaded`
      });
    } catch (error) {
      console.error("Error generating audio:", error);
      toast.error("Download failed", {
        description: "There was an error generating your audio file"
      });
    }
  };
  
  const getDisplayFrequency = (): number => {
    if (advancedSessionSteps && advancedSessionSteps.length > 0 && currentAdvancedStep < advancedSessionSteps.length) {
      return advancedSessionSteps[currentAdvancedStep].frequency;
    } else if (isProgressiveSession && progressiveData) {
      return activePreset?.frequency || 0;
    } else {
      return activePreset?.frequency || 0;
    }
  };
  
  const handleCreateAdvancedSession = (steps: SessionStep[], activeSounds?: AmbienceSound[]) => {
    if (!steps || steps.length === 0) {
      toast.error("Invalid session", {
        description: "The session must have at least one step"
      });
      return;
    }
    
    // Get current active ambient sounds if not provided
    const currentActiveSounds = activeSounds || ambientSounds
      .filter(sound => sound.volume > 0)
      .map(sound => ({
        id: sound.id,
        volume: sound.volume
      }));
    
    if (currentActiveSounds.length > 0) {
      setAmbientSounds(prevSounds => {
        return prevSounds.map(sound => {
          const activeSound = currentActiveSounds.find(s => s.id === sound.id);
          return activeSound ? { ...sound, volume: activeSound.volume } : sound;
        });
      });
    }
    
    const totalDuration = steps.reduce((total, step) => total + step.duration, 0);
    
    const advancedPreset: PresetInfo = {
      id: 'advanced',
      name: 'Advanced Session',
      frequency: steps[0].frequency,
      duration: totalDuration,
      description: `Multi-step session with ${steps.length} frequency segments`,
      color: steps[0].color || '#9B87F5',
      advancedSettings: {
        leftFrequency: steps[0].frequency,
        rightFrequency: steps[0].frequency,
        carrierFrequency: 200,
        leftWaveform: steps[0].leftWaveform || 'sine',
        rightWaveform: steps[0].rightWaveform || 'sine'
      },
      ...(currentActiveSounds.length > 0 && { ambientSounds: currentActiveSounds })
    };
    
    setAdvancedSessionSteps(steps);
    setCurrentAdvancedStep(0);
    
    // Stop any currently playing sounds
    if (isPlaying) {
      if (binauralGeneratorRef.current) {
        binauralGeneratorRef.current.stop();
      }
      if (ambientPlayerRef.current) {
        ambientPlayerRef.current.stopAll();
      }
      if (progressiveIntervalRef.current) {
        clearInterval(progressiveIntervalRef.current);
        progressiveIntervalRef.current = null;
      }
      if (advancedIntervalRef.current) {
        clearInterval(advancedIntervalRef.current);
        advancedIntervalRef.current = null;
      }
      setIsPlaying(false);
    }
    
    setActivePreset(advancedPreset);
    
    // Scroll to player
    if (playerRef.current) {
      playerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Ensure ambient sounds are properly initialized with current volumes
    if (ambientPlayerRef.current) {
      ambientSounds.forEach(sound => {
        if (sound.volume > 0) {
          const effectiveVolume = (sound.volume * masterVolume) / 100;
          ambientPlayerRef.current?.setVolume(sound.id, effectiveVolume);
        }
      });
    }
  };
  
  const startAdvancedSession = () => {
    if (!advancedSessionSteps || advancedSessionSteps.length === 0 || !binauralGeneratorRef.current) return;
    
    setCurrentAdvancedStep(0);
    
    const firstStep = advancedSessionSteps[0];
    binauralGeneratorRef.current.play(
      firstStep.frequency,
      firstStep.leftWaveform || 'sine',
      firstStep.rightWaveform || 'sine'
    );
    const stepVolume = firstStep.volume || 100;
    const effectiveVolume = (stepVolume * masterVolume) / 100;
    binauralGeneratorRef.current.setVolume(effectiveVolume);
    
    let currentStepIndex = 0;
    let totalElapsedTime = 0;
    
    if (advancedIntervalRef.current) {
      clearInterval(advancedIntervalRef.current);
    }
    
    advancedIntervalRef.current = window.setInterval(() => {
      totalElapsedTime += 1;
      
      const currentStepDurationInSeconds = advancedSessionSteps[currentStepIndex].duration * 60;
      
      if (totalElapsedTime >= currentStepDurationInSeconds) {
        currentStepIndex++;
        setCurrentAdvancedStep(currentStepIndex);
        
        totalElapsedTime = 0;
        
        if (currentStepIndex >= advancedSessionSteps.length) {
          if (advancedIntervalRef.current) {
            clearInterval(advancedIntervalRef.current);
            advancedIntervalRef.current = null;
          }
          handleSessionComplete();
          return;
        }
        
        const nextStep = advancedSessionSteps[currentStepIndex];
        if (binauralGeneratorRef.current) {
          binauralGeneratorRef.current.setFrequency(nextStep.frequency);
          binauralGeneratorRef.current.setWaveforms(
            nextStep.leftWaveform || 'sine',
            nextStep.rightWaveform || 'sine'
          );
          const stepVolume = nextStep.volume || 100;
          const effectiveVolume = (stepVolume * masterVolume) / 100;
          binauralGeneratorRef.current.setVolume(effectiveVolume);
          
          toast.info(`Step ${currentStepIndex + 1}`, {
            description: `Switching to ${nextStep.frequency.toFixed(1)} Hz with ${nextStep.leftWaveform || 'sine'}/${nextStep.rightWaveform || 'sine'} waveforms at ${nextStep.volume || 100}% volume for ${nextStep.duration} minutes`,
            duration: 3000
          });
        }
      }
    }, 1000) as unknown as number;
  };
  
  const handleSeek = (timeInSeconds: number) => {
    if (!activePreset) return;

    // Update the time remaining
    setTimeRemaining(activePreset.duration * 60 - timeInSeconds);

    // Seek ambient sounds
    if (ambientPlayerRef.current) {
      ambientPlayerRef.current.seekAll(timeInSeconds);
    }

    // Note: Binaural beats don't need seeking as they are generated in real-time
  };
  
  // Add ESC key handler
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isPlayMode) {
        togglePlayMode();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isPlayMode]);
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handlePreviewStart = () => {
    setIsPreviewing(true);
    
    // Start binaural beats if we have an active preset
    if (activePreset && binauralGeneratorRef.current) {
      if (activePreset.advancedSettings) {
        binauralGeneratorRef.current.play(
          activePreset.frequency,
          activePreset.advancedSettings.leftWaveform,
          activePreset.advancedSettings.rightWaveform,
          activePreset.advancedSettings.carrierFrequency
        );
      } else {
        binauralGeneratorRef.current.play(activePreset.frequency);
      }
      const effectiveVolume = (volume * masterVolume) / 100;
      binauralGeneratorRef.current.setVolume(effectiveVolume);
    }

    // Start ambient sounds with volume > 0
    if (ambientPlayerRef.current) {
      ambientSounds.forEach(sound => {
        if (sound.volume > 0) {
          const effectiveVolume = (sound.volume * masterVolume) / 100;
          ambientPlayerRef.current?.setVolume(sound.id, effectiveVolume);
          ambientPlayerRef.current?.playSound(sound.id);
        }
      });
    }

    // Stop preview after 15 seconds
    setTimeout(() => {
      handlePreviewStop();
    }, 15000);
  };

  const handlePreviewStop = () => {
    setIsPreviewing(false);
    
    // Stop binaural beats
    if (binauralGeneratorRef.current) {
      binauralGeneratorRef.current.stop();
    }
    
    // Stop all ambient sounds
    if (ambientPlayerRef.current) {
      ambientPlayerRef.current.stopAll();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header
        onStartTour={() => setIsTourOpen(true)}
        onCreateCustomSession={handleChatCustomSession}
        onSelectPreset={(presetId) => {
          const preset = ALL_PRESETS.find(p => p.id === presetId);
          if (preset) {
            handlePresetSelect(preset);
          }
        }}
      />
      <main className="container mx-auto px-4 py-8">
        <AnimatedHeader 
          title="BinauralFlow" 
          subtitle="Explore the power of binaural beats for focus, relaxation and meditation"
        />

        <div className="mt-16 space-y-8 text-center">
          <a 
            href="https://binauralflow.ai/learn" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <span className="text-blue-600 dark:text-blue-400 font-medium">
              Discover the power of Binaural Beats
            </span>
          </a>
          
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Enhance your mind with precision frequencies
          </h2>
          
          <p className="max-w-2xl mx-auto text-lg text-gray-600 dark:text-gray-300">
            Our binaural beats and custom soundscapes can help improve focus, creativity, relaxation and sleep quality through targeted brain stimulation
          </p>
        </div>

        <div ref={playerRef} className="mt-12">
          {activePreset && (
            <Player
              activePreset={activePreset}
              isPlaying={isPlaying}
              volume={volume}
              onVolumeChange={handleVolumeChange}
              masterVolume={masterVolume}
              onMasterVolumeChange={handleMasterVolumeChange}
              onPlayPause={handlePlayPause}
              onComplete={handleSessionComplete}
              onStartRecording={handleStartRecording}
              onStopRecording={handleStopRecording}
              onDirectDownload={handleDirectDownload}
              ambientSounds={ambientSounds}
              onAmbientVolumeChange={handleAmbientVolumeChange}
              onTogglePlayMode={togglePlayMode}
              isPlayMode={isPlayMode}
              advancedSettings={activePreset.advancedSettings}
              displayFrequency={activePreset.frequency}
              isAdvancedSession={isProgressiveSession}
              currentAdvancedStep={currentAdvancedStep}
              totalAdvancedSteps={advancedSessionSteps?.length}
              onSeek={handleSeek}
              onAmbientSeek={handleSeek}
              onClose={() => setActivePreset(null)}
            />
          )}
        </div>

        <div className="mt-12">
          <TabView
            presets={ALL_PRESETS}
            activePreset={activePreset}
            isPlaying={isPlaying}
            onSelectPreset={handlePresetSelect}
            onPlayPause={handlePlayPause}
            onCreateCustomSession={handleCreateCustomSession}
            onCreateProgressiveSession={handleCreateProgressiveSession}
            onSelectGuidedSession={handleSelectGuidedSession}
            onCreateAdvancedSession={handleCreateAdvancedSession}
            ambientSounds={ambientSounds}
            onAmbientVolumeChange={handleAmbientVolumeChange}
            playerRef={playerRef}
            isPlayMode={isPlayMode}
            value={activeTab}
            onValueChange={handleTabChange}
          />
        </div>
      </main>
      <MinimalFooter />
    </div>
  );
}
